import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from '../../lib/toast';
import { normalizeWhatsAppNumber, buildWaMeLink } from '../../lib/whatsapp';
import { API, auth, errMsg } from '../organization/finance/shared';
import { statusMeta } from '../organization/venue-booking/bookingStatusMeta';
import PaymentProofModal from '../organization/venue-booking/PaymentProofModal';

const ACTIONABLE_STATUSES = ['pending', 'pending_payment', 'pending_payment_verification'];

export default function VenueOwnerNotificationsPage() {
  const [items, setItems] = useState(null);
  const [proofBooking, setProofBooking] = useState(null);

  const load = useCallback(async () => {
    try {
      const results = await Promise.all(ACTIONABLE_STATUSES.map((status) => axios.get(`${API}/venues/bookings/search`, { ...auth(), params: { status } }).then((r) => r.data.items || [])));
      const merged = results.flat().sort((a, b) => `${a.date}${a.start_time}`.localeCompare(`${b.date}${b.start_time}`));
      setItems(merged);
    } catch { setItems([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (b, action, msg) => {
    try {
      await axios.post(`${API}/venues/${b.venue_id}/bookings/${b.id}/${action}`, {}, auth());
      toast.success(msg);
      load();
    } catch (e) { toast.error(errMsg(e, 'فشل تنفيذ الإجراء')); }
  };

  return (
    <div className="space-y-4" data-testid="venue-owner-notifications-page">
      <p className="text-sm text-slate-500">طلبات الحجز والدفعات التي تحتاج إجراء منك، مع إمكانية إرسال رسالة واتساب جاهزة للعميل</p>
      {items === null ? (
        <div className="h-40 animate-pulse rounded-2xl bg-slate-100" />
      ) : items.length === 0 ? (
        <p className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-400" data-testid="venue-owner-notifications-empty">لا توجد طلبات معلّقة حالياً 🎉</p>
      ) : (
        <div className="space-y-2" data-testid="venue-owner-notifications-list">
          {items.map((b) => {
            const waNum = normalizeWhatsAppNumber(b.customer_phone);
            const meta = statusMeta(b.status);
            return (
              <div key={b.id} className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-amber-200 bg-amber-50/50 p-4" data-testid={`venue-owner-notification-row-${b.id}`}>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-bold text-slate-900">{b.venue_name} {b.court_label ? `— ${b.court_label}` : ''}</p>
                    <span className={`rounded-full border px-2 py-0.5 text-[11px] font-bold ${meta.badge}`}>{meta.label}</span>
                  </div>
                  <p className="text-xs text-slate-600">{b.date} • {b.start_time}-{b.end_time} • {b.customer_name || 'بلا اسم'} • <span dir="ltr">{b.customer_phone || '—'}</span></p>
                </div>
                <div className="flex gap-1.5">
                  {b.status === 'pending' && (
                    <>
                      <button onClick={() => act(b, 'approve', 'تم قبول الحجز')} className="rounded-lg border border-emerald-200 bg-white px-3 py-1.5 text-xs font-bold text-emerald-700" data-testid={`venue-owner-notification-approve-${b.id}`}>قبول</button>
                      <button onClick={() => act(b, 'reject', 'تم رفض الحجز')} className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-bold text-red-600" data-testid={`venue-owner-notification-reject-${b.id}`}>رفض</button>
                    </>
                  )}
                  {(b.status === 'pending_payment' || b.status === 'pending_payment_verification') && (
                    <>
                      {b.status === 'pending_payment_verification' && (
                        <>
                          <button onClick={() => setProofBooking(b)} className="rounded-lg border border-blue-200 bg-white px-3 py-1.5 text-xs font-bold text-blue-700" data-testid={`venue-owner-notification-view-proof-${b.id}`}>عرض الإثبات</button>
                          <button onClick={() => act(b, 'reject-proof', 'تم رفض الإثبات')} className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-bold text-red-600" data-testid={`venue-owner-notification-reject-proof-${b.id}`}>رفض الإثبات</button>
                        </>
                      )}
                      <button onClick={() => act(b, 'confirm-payment', 'تم تأكيد استلام الدفع')} className="rounded-lg border border-emerald-200 bg-white px-3 py-1.5 text-xs font-bold text-emerald-700" data-testid={`venue-owner-notification-confirm-payment-${b.id}`}>تأكيد استلام الدفع</button>
                    </>
                  )}
                  {waNum && (
                    <a href={buildWaMeLink(waNum, `مرحباً ${b.customer_name || ''}، بخصوص طلب حجزك في ${b.venue_name} يوم ${b.date} من ${b.start_time} إلى ${b.end_time}.`)} target="_blank" rel="noreferrer" className="rounded-lg border border-emerald-200 bg-white px-3 py-1.5 text-xs font-bold text-emerald-700" data-testid={`venue-owner-notification-whatsapp-${b.id}`}>واتساب</a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {proofBooking && <PaymentProofModal venueId={proofBooking.venue_id} booking={proofBooking} onClose={() => setProofBooking(null)} />}
    </div>
  );
}
